// FORGED Ed Systems — DEPLOY005 — v0.5.0 — IntakePage.tsx
// Last touched: 2026-05-20
//
// Orchestrator for the 6-section intake form. Holds form state, manages
// step navigation, validates each step before allowing Continue, and
// submits to /api/intake-save on the final step.
//
// DEPLOY005 — saveIntake now sends target_code/target_process/target_position
// instead of a single bundled target_certification.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import { saveIntake, startStrategy } from "./api";
import IntakeStep1OperatingMode from "./IntakeStep1OperatingMode";
import IntakeStep2Curriculum from "./IntakeStep2Curriculum";
import IntakeStep3Program from "./IntakeStep3Program";
import IntakeStep4Cohort from "./IntakeStep4Cohort";
import IntakeStep5CurrentState from "./IntakeStep5CurrentState";
import IntakeStep6Optional from "./IntakeStep6Optional";
import IntakeNavigation from "./IntakeNavigation";
import { INITIAL_INTAKE_STATE, visibleStepCount } from "./intakeTypes";
import type { IntakeFormState, CohortCurrentState } from "./intakeTypes";
import {
  validateStep1,
  validateStep2,
  validateStep3,
  validateStep4,
  validateStep5,
} from "./intakeValidation";

function emptyCurrentState(): CohortCurrentState {
  return {
    weeks_remaining_in_term: 18,
    cohort_segments: [
      {
        segment_label: "",
        student_count: 1,
        current_position_capability: "2F_passing",
        current_process_capability: ["SMAW"],
      },
    ],
    notable_blockers: [],
  };
}

export default function IntakePage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [state, setState] = useState<IntakeFormState>(INITIAL_INTAKE_STATE);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pilotCode, setPilotCode] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !session) {
      navigate("/sign-in");
    }
  }, [loading, session, navigate]);

  useEffect(() => {
    if (!session) return;
    var cancelled = false;
    supabase
      .from("teacher_accounts")
      .select("pilot_code_redeemed")
      .eq("auth_user_id", session.user.id)
      .single()
      .then((res) => {
        if (cancelled) return;
        if (res.data && res.data.pilot_code_redeemed) {
          setPilotCode(res.data.pilot_code_redeemed);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  if (loading) {
    return (
      <main className="container">
        <p>Loading…</p>
      </main>
    );
  }

  if (!session) {
    return null;
  }

  const totalSteps = visibleStepCount(state.operating_mode);
  const skipsStep5 = state.operating_mode === "start_of_year";

  function goNext() {
    setErrorMsg(null);

    var err: string | null = null;
    if (step === 1) err = validateStep1(state);
    if (step === 2) err = validateStep2(state);
    if (step === 3) err = validateStep3(state);
    if (step === 4) err = validateStep4(state);
    if (step === 5 && !skipsStep5) err = validateStep5(state);

    if (err) {
      setErrorMsg(err);
      return;
    }

    if (step === 4 && !skipsStep5 && !state.current_state) {
      setState((prev) => ({ ...prev, current_state: emptyCurrentState() }));
    }

    if (step === 4 && skipsStep5) {
      setStep(6);
      return;
    }

    setStep(step + 1);
  }

  function goBack() {
    setErrorMsg(null);
    if (step === 6 && skipsStep5) {
      setStep(4);
      return;
    }
    setStep(step - 1);
  }

  async function handleSubmit() {
    setErrorMsg(null);

    var err: string | null =
      validateStep1(state) ||
      validateStep2(state) ||
      validateStep3(state) ||
      validateStep4(state);
    if (!skipsStep5) {
      err = err || validateStep5(state);
    }
    if (err) {
      setErrorMsg(err);
      return;
    }

    setSubmitting(true);
    const saveRes = await saveIntake({
      submission_data: state,
      evaluation_curriculum: state.evaluation_curriculum!,
      operating_mode: state.operating_mode!,
      target_code: state.cohort.target_code,
      target_process: state.cohort.target_process,
      target_position: state.cohort.target_position,
    });

    if (!saveRes.ok) {
      setSubmitting(false);
      setErrorMsg(saveRes.message);
      return;
    }

    // Trigger background strategy generation. Fire and redirect to /r/<report_id>
    // where the user sees progress + the final plan when it's ready.
    const startRes = await startStrategy(saveRes.data.submission_id);
    setSubmitting(false);

    if (!startRes.ok) {
      setErrorMsg(
        "Your intake was saved, but plan generation couldn't start: " +
          startRes.message +
          ". Your data is in your dashboard."
      );
      return;
    }

    // Trigger the background function from the browser. Netlify's edge
    // recognizes the -background suffix and ACKs with 202 immediately.
    //
    // CRITICAL: use keepalive + await. The browser aborts in-flight fetches
    // when the page unloads (navigate fires immediately after), so without
    // keepalive the request can be cancelled before it reaches Netlify.
    // The await ensures the request at least starts being sent before
    // navigation begins. Both belt and suspenders.
    try {
      await fetch("/api/strategy-generate-background", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          report_id: startRes.data.report_id,
          submission_id: saveRes.data.submission_id,
        }),
        keepalive: true,
      });
    } catch {
      // If the browser can't reach the background function, the user will
      // see status='queued' indefinitely on the ReportViewPage. The intake
      // is still saved; they can contact support to resume.
    }

    navigate("/r/" + startRes.data.report_id);
  }

  const displayStep = skipsStep5 && step === 6 ? 5 : step;

  return (
    <main className="container">
      <header className="brand-header">
        <h1>forgedsight</h1>
        <p className="tagline">New strategic plan</p>
      </header>

      <div className="intake-content">
        {step === 1 && (
          <IntakeStep1OperatingMode
            value={state.operating_mode}
            onChange={(m) => {
              const nextCurrentState = m === "start_of_year" ? null : state.current_state;
              setState({ ...state, operating_mode: m, current_state: nextCurrentState });
            }}
          />
        )}
        {step === 2 && (
          <IntakeStep2Curriculum
            value={state.evaluation_curriculum}
            pilotCodeRedeemed={pilotCode}
            onChange={(c) => setState({ ...state, evaluation_curriculum: c })}
          />
        )}
        {step === 3 && (
          <IntakeStep3Program
            instructor={state.instructor}
            equipment={state.equipment}
            calendar={state.calendar}
            onChangeInstructor={(b) => setState({ ...state, instructor: b })}
            onChangeEquipment={(b) => setState({ ...state, equipment: b })}
            onChangeCalendar={(b) => setState({ ...state, calendar: b })}
          />
        )}
        {step === 4 && (
          <IntakeStep4Cohort
            value={state.cohort}
            onChange={(c) => setState({ ...state, cohort: c })}
          />
        )}
        {step === 5 && !skipsStep5 && state.current_state && (
          <IntakeStep5CurrentState
            rosterSize={state.cohort.roster_size}
            value={state.current_state}
            onChange={(cs) => setState({ ...state, current_state: cs })}
          />
        )}
        {step === 6 && (
          <IntakeStep6Optional
            value={state.optional_notes}
            onChange={(v) => setState({ ...state, optional_notes: v })}
          />
        )}

        {errorMsg && <div className="error-banner">{errorMsg}</div>}

        <IntakeNavigation
          currentStep={displayStep}
          totalSteps={totalSteps}
          isLastStep={step === 6}
          isSubmitting={submitting}
          onBack={goBack}
          onContinue={goNext}
          onSubmit={handleSubmit}
        />
      </div>
    </main>
  );
}
